import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import { Save, Bot, Eye, EyeOff, KeyRound, Cpu, Zap, CheckCircle2, AlertCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  {
    key: "anthropic",
    label: "Anthropic (Direct)",
    sub: "Use your own Anthropic API key — recommended for VPS deployments.",
    keyHint: "sk-ant-... (get one at console.anthropic.com)",
  },
  {
    key: "emergent",
    label: "Emergent Universal Key",
    sub: "Use the Emergent LLM key — works with Anthropic, OpenAI and Gemini models.",
    keyHint: "Emergent universal key (Profile → Universal Key)",
  },
];

export default function ConfigAIChat() {
  const [form, setForm] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/ai-chat");
        setForm(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load AI chat settings");
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const modelsForProvider = () => {
    if (!form) return [];
    const map = form.models_by_provider || {};
    return map[form.provider] || [];
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setTestResult(null);
    try {
      const payload = {
        enabled: !!form.enabled,
        provider: form.provider,
        model: form.model,
      };
      // Only send api_key if user typed a new one — empty string = keep existing
      if (newKey.trim()) payload.api_key = newKey.trim();
      const { data } = await api.put("/settings/ai-chat", payload);
      setForm(data);
      setNewKey("");
      toast.success("AI live-chat settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!form) return;
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        provider: form.provider,
        model: form.model,
      };
      // If user typed a new key, test that key (don't require save first)
      if (newKey.trim()) payload.api_key = newKey.trim();
      const { data } = await api.post("/settings/ai-chat/test", payload);
      setTestResult(data);
      if (data.ok) {
        toast.success(`Connection OK · ${data.duration_ms} ms · model "${data.model}"`);
      } else {
        toast.error(`Test failed: ${data.error || "no reply"}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Test failed";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (!form) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;
  }

  const models = modelsForProvider();

  return (
    <div data-testid="config-ai-chat-page">
      <PageHeader
        title="Configuration"
        subtitle="Site-wide configuration · AI Live Chat (Max_Ai assistant)"
      />
      <ConfigTabs />

      <form
        onSubmit={(e) => { e.preventDefault(); save(); }}
        className="bg-white border border-gray-200 rounded-sm p-6 max-w-5xl space-y-6"
        data-testid="config-ai-chat-form"
      >
        {/* Top toggle */}
        <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-sm bg-gray-50">
          <div className="h-10 w-10 rounded-sm bg-emerald-500/10 text-emerald-600 inline-flex items-center justify-center shrink-0">
            <Bot size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-gray-900">Enable Max_Ai assistant</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Players can chat with the in-house AI support bot from the floating widget. <b>Disabling this also hides the green Max_Ai bubble on every public page</b> — turn it on (and add a key below) to bring the bubble back.
                </div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => set("enabled", e.target.checked)}
                  className="sr-only peer"
                  data-testid="ai-enabled"
                />
                <span className="relative w-11 h-6 bg-gray-300 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Provider */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            <span className="inline-flex items-center gap-1.5"><KeyRound size={12} /> Provider</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PROVIDERS.map((p) => {
              const active = form.provider === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => set("provider", p.key)}
                  data-testid={`ai-provider-${p.key}`}
                  className={`text-left p-4 rounded-sm border transition-all ${active ? "border-emerald-500 bg-emerald-50/40 ring-1 ring-emerald-500/30" : "border-gray-200 hover:border-emerald-300"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-gray-900">{p.label}</div>
                    {active && <CheckCircle2 size={16} className="text-emerald-600" />}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{p.sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* API key */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            <span className="inline-flex items-center gap-1.5"><KeyRound size={12} /> API key</span>
          </label>
          <div className="flex items-stretch gap-2">
            <div className="relative flex-1">
              <input
                type={showKey ? "text" : "password"}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder={form.api_key_set ? `Current: ${form.api_key_masked} — paste new to rotate` : (PROVIDERS.find((p) => p.key === form.provider)?.keyHint || "Paste your API key")}
                spellCheck={false}
                autoComplete="off"
                className="w-full h-10 px-3 pr-10 border border-gray-200 rounded-sm text-sm font-mono focus:outline-none focus:border-emerald-500"
                data-testid="ai-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900"
                data-testid="ai-toggle-key-visible"
              >
                {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div className="text-[11px] text-gray-500 mt-1.5">
            {form.api_key_set ? (
              <>A key is currently saved. Leave field empty to keep it; paste a new value to rotate.</>
            ) : (
              <>No key saved yet — please paste your provider key. Stored only in your database, never logged.</>
            )}
          </div>
        </div>

        {/* Model */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            <span className="inline-flex items-center gap-1.5"><Cpu size={12} /> Model</span>
          </label>
          <select
            value={form.model || ""}
            onChange={(e) => set("model", e.target.value)}
            className="w-full md:w-[420px] h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
            data-testid="ai-model-select"
          >
            {models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
            {/* Allow custom model name override too */}
            {form.model && !models.includes(form.model) && (
              <option value={form.model}>{form.model} (custom)</option>
            )}
          </select>
          <div className="text-[11px] text-gray-500 mt-1.5">
            Switching providers automatically switches the available model list. Anthropic is recommended for chat quality; Emergent unlocks GPT and Gemini.
          </div>
        </div>

        {/* Test connection */}
        <div className="p-4 border border-gray-200 rounded-sm bg-gray-50">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-semibold text-gray-900 inline-flex items-center gap-1.5">
                <Zap size={15} className="text-amber-500" /> Live connection test
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Sends a tiny "Reply OK" prompt to the selected model with the typed/saved key. Doesn't deduct production traffic.
              </div>
            </div>
            <button
              type="button"
              onClick={testConnection}
              disabled={testing}
              className="h-10 px-4 border border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
              data-testid="ai-test-btn"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {testing ? "Testing..." : "Test connection"}
            </button>
          </div>

          {testResult && (
            <div
              className={`mt-3 rounded-sm border p-3 text-sm flex items-start gap-2 ${testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`}
              data-testid="ai-test-result"
            >
              {testResult.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}
              <div className="flex-1 min-w-0">
                {testResult.ok ? (
                  <>
                    <div className="font-semibold">Connection successful</div>
                    <div className="text-xs mt-0.5">
                      Model: <code className="bg-white/60 px-1 rounded">{testResult.model}</code> · {testResult.duration_ms} ms · reply: <em>"{testResult.reply}"</em>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold">Connection failed</div>
                    <div className="text-xs mt-0.5 break-all">{testResult.error || "No reply"}</div>
                  </>
                )}
              </div>
            </div>
          )}

          {form.last_test_at && !testResult && (
            <div className="mt-3 text-[11px] text-gray-500">
              Last test: {new Date(form.last_test_at).toLocaleString()} — {form.last_test_ok ? <span className="text-emerald-600 font-semibold">OK</span> : <span className="text-red-600 font-semibold">FAILED</span>}
              {!form.last_test_ok && form.last_test_error && <span> · {form.last_test_error}</span>}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Last updated: {form.updated_at ? new Date(form.updated_at).toLocaleString() : "—"}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="ai-save-btn"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save AI Chat Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
